import { useState } from 'react';
import {
    BookCopy,
    Calendar,
    ChevronDown,
    ChevronLeft,
    CircleOff,
    Ellipsis,
    Eye,
    FileDown,
    FileUp,
    Globe,
    LoaderCircle,
    Trash2,
} from 'lucide-react';
import { useHistory } from 'react-router-dom';

import DuplicateDialog from 'components/storefront/components/DuplicateDialog';
import { useDataLoader } from 'components/storefront/StorefrontConfigEditor/providers/DataLoaderProvider';
import {
    useFormControls,
    useFormState,
} from 'components/storefront/StorefrontConfigEditor/providers/FormStateProvider';
import { Badge } from 'shadcn/components/ui/Badge';
import { Button } from 'shadcn/components/ui/Button';
import { ButtonGroup } from 'shadcn/components/ui/ButtonGroup';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from 'shadcn/components/ui/Dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from 'shadcn/components/ui/DropdownMenu';
import { Input } from 'shadcn/components/ui/Input';
import { cn } from 'shadcn/lib/utils';
import { formatDateString } from 'utils/dateUtils/dateUtils';

import BulkApplyDialog from './BulkApplyDialog';
import UploadProductCSVDialog from './UploadProductCSVDialog';

/**
 * Header for the Storefront Configuration Editor.
 *
 * @returns {JSX.Element} The editor header component
 */
function EditorHeader() {
    const { packGroupDate } = useDataLoader();
    const { formData } = useFormState();
    const {
        deleteConfig,
        downloadCarouselCSV,
        duplicateConfig,
        invalidProductCount,
        loadingServerData,
        removeInvalidProducts,
        toggleDuplicateSlicesDialog,
        updateConfig,
        uploadProductCSV,
    } = useFormControls();

    const [showPublishDialog, setShowPublishDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showDownloadCarouselCSVDialog, setShowDownloadCarouselCSVDialog] = useState(false);
    const [showUploadProductCSVDialog, setShowUploadProductCSVDialog] = useState(false);
    const [showDuplicateDialog, setShowDuplicateDialog] = useState();
    const [showBulkApplyDialog, setShowBulkApplyDialog] = useState(false);

    // Snapshot state when dialog is opened to prevent text from changing before closing upon confirmation
    const [wasPublished, setWasPublished] = useState(false);

    const history = useHistory();

    const handleBack = () => {
        history.goBack();
    };

    const handleDownloadCarouselCSVClick = () => {
        downloadCarouselCSV(formData);
        setShowDownloadCarouselCSVDialog(false);
    };

    const handlePublishClick = () => {
        setWasPublished(!!formData?.published);
        setShowPublishDialog(true);
    };

    const handleUploadProductCSVClick = (file) => {
        uploadProductCSV(file, formData);
        setShowUploadProductCSVDialog(false);
    };

    const handleDuplicateConfigClick = async (name, packGroupID) => {
        await duplicateConfig(formData, name, packGroupID);
        setShowDuplicateDialog(false);
    };

    return (
        <>
            <div className="w-full h-13" />
            <div className="fixed top-[64px] flex justify-center gap-2 w-screen border-b pt-5 pb-4 px-4 -ml-5 bg-[#F6F6F6] z-50">
                <div className="flex-1 flex items-center gap-4 w-full max-w-7xl">
                    <div className=" flex-1 flex items-center gap-2 min-w-0">
                        <Button onClick={handleBack} variant="outline" type="button">
                            <ChevronLeft /> Back
                        </Button>
                        <div
                            className={cn(
                                'shrink-0 size-2 rounded-full ml-3 bg-yellow-500',
                                formData?.published && 'bg-green-500 animate-pulse-animation',
                            )}
                        />
                        <Input
                            name="name"
                            disabled={loadingServerData}
                            onChange={(e) => updateConfig({ name: e.target.value })}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    setTimeout(() => {
                                        e.target.blur();
                                    }, 0);
                                }
                            }}
                            value={formData?.name ?? ''}
                            className="w-auto min-w-25 max-w-full text-lg! font-bold field-sizing-content truncate border-none shadow-none hover:bg-border transition-colors focus:bg-background"
                        />
                        <p className="text-nowrap text-xs text-gray-500">ID: {formData?.id ?? 'N/A'}</p>
                    </div>

                    <div className="flex items-center gap-3">
                        <p className="text-xs text-gray-500">
                            Last updated on{' '}
                            {formData?.updatedAt
                                ? formatDateString(formData.updatedAt, false, true, false, false, true)
                                : 'N/A'}
                        </p>
                        <ButtonGroup>
                            <Button
                                disabled={loadingServerData}
                                name="save"
                                form="storefront-config-form"
                                type="submit"
                                variant="outline"
                                formNoValidate={!formData?.published}
                            >
                                Save changes
                            </Button>
                            <SaveChangesDropdown
                                disabled={loadingServerData}
                                onApply={() => setShowBulkApplyDialog(true)}
                            />
                        </ButtonGroup>
                        <Button
                            disabled={loadingServerData}
                            onClick={handlePublishClick}
                            type="button"
                            variant="default"
                        >
                            {formData?.published ? (
                                <>
                                    <CircleOff /> Unpublish
                                </>
                            ) : (
                                <>
                                    <Globe /> Publish
                                </>
                            )}
                        </Button>
                        <OptionsDropdown
                            deleteDisabled={!formData?.id}
                            disabled={loadingServerData}
                            invalidProductCount={invalidProductCount}
                            onDelete={() => setShowDeleteDialog(true)}
                            onDuplicateConfig={() => setShowDuplicateDialog(formData)}
                            onDuplicateSlices={() => toggleDuplicateSlicesDialog(null)}
                            onDownloadCarouselCSV={() => setShowDownloadCarouselCSVDialog(true)}
                            onRemoveInvalidProducts={removeInvalidProducts}
                            onUploadProductCSV={() => setShowUploadProductCSVDialog(true)}
                            previewDisabled={!formData?.id}
                            previewUrl={`https://www.misfitsmarket.com/storefront-config/${formData?.id}`}
                        />
                    </div>
                </div>
            </div>
            <PublishDialog
                isOpen={showPublishDialog}
                onClose={() => setShowPublishDialog(false)}
                onConfirm={() => setShowPublishDialog(false)}
                isPublished={wasPublished}
            />
            <DeleteDialog
                isOpen={showDeleteDialog}
                onClose={() => setShowDeleteDialog(false)}
                onConfirm={() => deleteConfig(formData.id)}
            />
            <UploadProductCSVDialog
                isOpen={showUploadProductCSVDialog}
                onClose={() => setShowUploadProductCSVDialog(false)}
                onConfirm={handleUploadProductCSVClick}
            />
            <DownloadCarouselCSVDialog
                isOpen={showDownloadCarouselCSVDialog}
                onClose={() => setShowDownloadCarouselCSVDialog(false)}
                onConfirm={handleDownloadCarouselCSVClick}
            />
            <DuplicateDialog
                configId={formData?.id}
                initialDate={packGroupDate}
                initialName={formData?.name}
                isLoading={loadingServerData}
                isOpen={showDuplicateDialog}
                onClose={() => setShowDuplicateDialog(false)}
                onConfirm={handleDuplicateConfigClick}
            />
            <BulkApplyDialog isOpen={showBulkApplyDialog} onClose={() => setShowBulkApplyDialog(false)} />
        </>
    );
}

function SaveChangesDropdown({ disabled, onApply }) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button disabled={disabled} variant="outline" className="px-2">
                    <ChevronDown />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onApply}>
                    <Calendar />
                    Apply to other configurations
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

/**
 * Dropdown menu for additional configuration options.
 *
 * @param {Object} props
 * @param {boolean} props.deleteDisabled - Whether the delete button is disabled
 * @param {boolean} props.disabled - Whether the dropdown is disabled
 * @param {number} props.invalidProductCount - Number of invalid products in the configuration
 * @param {() => void} props.onDelete - Callback function to handle configuration deletion
 * @param {() => void} props.onDuplicateConfig - Callback function to handle configuration duplication
 * @param {() => void} props.onDuplicateSlices - Callback function to handle slice duplication
 * @param {() => void} props.onDownloadCarouselCSV - Callback function to handle download of carousels CSV
 * @param {() => void} props.onRemoveInvalidProducts - Callback function to handle removal of invalid products
 * @param {() => void} props.onUploadProductCSV - Callback function to handle upload of product CSV
 * @param {boolean} props.previewDisabled - Whether the preview button is disabled
 * @param {string} props.previewUrl - URL for the preview link
 * @returns {JSX.Element} The options dropdown menu component
 */
function OptionsDropdown({
    deleteDisabled,
    disabled,
    invalidProductCount,
    onDelete,
    onDownloadCarouselCSV,
    onDuplicateConfig,
    onDuplicateSlices,
    onRemoveInvalidProducts,
    onUploadProductCSV,
    previewDisabled,
    previewUrl,
}) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    disabled={disabled}
                    variant="ghost"
                    size="icon"
                    className="relative hover:bg-border data-[state=open]:bg-border data-[state=open]:hover:bg-border"
                >
                    <Ellipsis />
                    {invalidProductCount > 0 && (
                        <Badge
                            variant="destructive"
                            className="absolute top-0 right-0 transform translate-x-1/3 -translate-y-1/3 rounded-full w-5 h-5 font-bold"
                        >
                            !
                        </Badge>
                    )}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                <DropdownMenuItem
                    disabled={previewDisabled}
                    onClick={() => {
                        window.open(previewUrl, '_blank');
                    }}
                >
                    <Eye />
                    Preview {previewDisabled && <>(save to enable)</>}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onDuplicateSlices}>
                    <BookCopy />
                    Duplicate Slices
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onDuplicateConfig}>
                    <BookCopy />
                    Duplicate Configuration
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onUploadProductCSV}>
                    <FileUp />
                    Upload Product CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onDownloadCarouselCSV}>
                    <FileDown />
                    Download Carousel CSV
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                    disabled
                    variant="destructive"
                    onClick={onRemoveInvalidProducts}
                    // disabled={invalidProductCount <= 0}
                >
                    <Trash2 />
                    Remove all invalid items
                    {invalidProductCount === -1 ? (
                        <LoaderCircle className="size-4 animate-spin" />
                    ) : (
                        ` (${invalidProductCount})`
                    )}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem variant="destructive" onClick={onDelete} disabled={deleteDisabled}>
                    <Trash2 />
                    Delete
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

/**
 * Dialog for confirming publish/unpublish actions.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the dialog is currently open
 * @param {() => void} props.onClose - Callback function to handle dialog closure
 * @param {() => void} props.onConfirm - Callback function to handle action confirmation
 * @param {boolean} props.isPublished - Current publication status of the configuration
 * @returns {JSX.Element} The publish dialog component
 */
function PublishDialog({ isOpen, isPublished, onClose, onConfirm }) {
    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{isPublished ? 'Unpublish Configuration' : 'Publish Configuration'}</DialogTitle>
                    <DialogDescription>
                        {isPublished
                            ? 'Are you sure you want to unpublish this configuration?'
                            : 'Are you sure you want to publish this configuration?'}
                        <br />
                        {isPublished ? 'This will remove it from production.' : 'This will turn it on for production.'}
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        onClick={onConfirm}
                        form="storefront-config-form"
                        name="publish"
                        type="submit"
                        variant="default"
                        formNoValidate={isPublished}
                    >
                        Confirm
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/**
 * Dialog for confirming configuration deletion.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the dialog is currently open
 * @param {() => void} props.onClose - Callback function to handle dialog closure
 * @param {() => void} props.onConfirm - Callback function to handle deletion confirmation
 * @returns {JSX.Element} The delete dialog component
 */
function DeleteDialog({ isOpen, onClose, onConfirm }) {
    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Delete Configuration</DialogTitle>
                    <DialogDescription>
                        Are you sure you want to delete this configuration?
                        <br />
                        This action cannot be undone.
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button variant="default" onClick={onConfirm}>
                        Confirm
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function DownloadCarouselCSVDialog({ isOpen, onClose, onConfirm }) {
    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Download Carousel CSV</DialogTitle>
                    <DialogDescription>
                        Are you sure you want to download a CSV of carousels for this configuration? The file will
                        reflect the latest saved state.
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button variant="default" onClick={onConfirm}>
                        Download
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default EditorHeader;
